import React from "react";
import { useParams, Link } from "react-router-dom";
import { guitarSyllabus } from "../mock/guitarSyllabus";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Download } from "lucide-react";

export default function GuitarLevelDetail() {
  const { levelNumber } = useParams();
  const numericLevel = Number(levelNumber);
  const levelData = guitarSyllabus[numericLevel];

  const handleSavePdf = async () => {
    // FRONTEND-ONLY: Generate a simple PDF using the current syllabus text
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "a4" });

    const marginX = 48;
    let cursorY = 60;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Global Music Conservatory – Guitar Syllabus", marginX, cursorY);

    cursorY += 22;
    doc.setFontSize(12);
    doc.text(levelData.title, marginX, cursorY);

    cursorY += 18;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    const addWrappedText = (text, x, y, maxWidth) => {
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, y);
      return y + lines.length * 13;
    };

    cursorY = addWrappedText(levelData.objective, marginX, cursorY, 500) + 12;

    // Practical sections
    doc.setFont("helvetica", "bold");
    doc.text("Practical syllabus (guitar)", marginX, cursorY);
    cursorY += 16;

    doc.setFont("helvetica", "normal");
    levelData.practicalSections.forEach((section) => {
      cursorY = addWrappedText(section.title, marginX, cursorY, 500);
      section.items.forEach((item) => {
        cursorY = addWrappedText(`• ${item}`, marginX + 14, cursorY, 480);
      });
      cursorY += 10;
      if (cursorY > 760) {
        doc.addPage();
        cursorY = 60;
      }
    });

    // Theory sections
    doc.addPage();
    cursorY = 60;
    doc.setFont("helvetica", "bold");
    doc.text("Theory syllabus", marginX, cursorY);
    cursorY += 16;

    doc.setFont("helvetica", "normal");
    levelData.theorySections.forEach((section) => {
      cursorY = addWrappedText(section.title, marginX, cursorY, 500);
      section.items.forEach((item) => {
        cursorY = addWrappedText(`• ${item}`, marginX + 14, cursorY, 480);
      });
      cursorY += 10;
      if (cursorY > 760) {
        doc.addPage();
        cursorY = 60;
      }
    });

    // Question bank (if present)
    if (levelData.questionBank) {
      doc.addPage();
      cursorY = 60;
      doc.setFont("helvetica", "bold");
      doc.text(levelData.questionBank.title, marginX, cursorY);
      cursorY += 18;

      doc.setFont("helvetica", "normal");
      levelData.questionBank.sections.forEach((section) => {
        cursorY = addWrappedText(section.label, marginX, cursorY, 500);
        section.items.forEach((item) => {
          cursorY = addWrappedText(`• ${item}`, marginX + 14, cursorY, 480);
        });
        cursorY += 10;
        if (cursorY > 760) {
          doc.addPage();
          cursorY = 60;
        }
      });
    }

    const fileName = `Guitar-Level-${numericLevel}-Syllabus.pdf`;
    doc.save(fileName);
  };

  if (!levelData) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-700">The requested level could not be found.</p>
        <Button asChild variant="outline" className="border-[#d0bf99] bg-white px-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1f2738] hover:bg-[#f5ebd9]">
          <Link to="/guitar-syllabus">Back to guitar syllabus</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section className="flex flex-wrap items-center justify-between gap-3 border-b border-[#ddcaa4] pb-3 print:hidden">
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8c7648]">
            Guitar certification
          </p>
          <h1 className="text-xl font-semibold tracking-tight text-[#182033]">
            {levelData.title}
          </h1>
          <p className="max-w-2xl text-sm leading-relaxed text-[#4b4a46]">
            {levelData.objective}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            asChild
            variant="outline"
            className="border-[#d0bf99] bg-white px-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1f2738] hover:bg-[#f5ebd9]"
          >
            <Link to="/guitar-syllabus">Back to levels 1–6</Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleSavePdf}
            className="inline-flex items-center gap-1 border-[#d0bf99] bg-[#1b2236] px-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#f8f2e6] hover:bg-[#151b2b]"
          >
            <Download className="h-3 w-3" />
            <span>Save as PDF</span>
          </Button>
        </div>
      </section>

      <section className="space-y-4">
        <Card className="border-[#e1d2b3] bg-[#fdf8ef] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-tight text-[#182033]">
              Practical syllabus (guitar)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 px-4 pb-4 text-sm leading-relaxed text-[#4b4a46]">
            {levelData.practicalSections.map((section) => (
              <div key={section.title} className="space-y-1.5">
                <p className="text-sm font-semibold text-[#182033]">{section.title}</p>
                <ul className="ml-4 list-disc space-y-0.5 text-sm">
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      <section className="space-y-4">
        <Card className="border-[#e1d2b3] bg-[#fdf8ef] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-tight text-[#182033]">
              Theory syllabus
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 px-4 pb-4 text-sm leading-relaxed text-[#4b4a46]">
            {levelData.theorySections.map((section) => (
              <div key={section.title} className="space-y-1.5">
                <p className="text-sm font-semibold text-[#182033]">{section.title}</p>
                <ul className="ml-4 list-disc space-y-0.5 text-sm">
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>

      {levelData.questionBank && (
        <section className="space-y-4">
          <Card className="border-[#e1d2b3] bg-[#fdf8ef] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold tracking-tight text-[#182033]">
                {levelData.questionBank.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 px-4 pb-4 text-sm leading-relaxed text-[#4b4a46]">
              {levelData.questionBank.sections.map((section) => (
                <div key={section.label} className="space-y-1.5">
                  <p className="text-sm font-semibold text-[#182033]">{section.label}</p>
                  <ul className="ml-4 list-disc space-y-0.5 text-sm">
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
